import type { Joi } from '@ksyos/koi';
import { DateTime } from '@ksyos/date-time';
import { Koi } from '@ksyos/koi';
import {
    ActiveOrInactive,
    MatchType,
    OrganizationRelationshipType,
    OrganizationType,
    ServiceCode,
    State,
} from '@common/enums';
import { OrganizationKgoStatus } from '@common/enums/organizationKgoStatus';
import { OrganizationStructureType } from '@common/enums/organizationStructureType';
import { hasActiveItem } from '@common/utils/dates';
import { isNonNullable } from '@common/utils/nonNullableTypeUtils';
import { pickKeysAndCreateSchema } from '@common/utils/pickKeysAndCreateSchema';
import { kvkNumberRegex } from '@common/utils/regularExpressions';
import { emptyUuid, type Uuid } from '@common/utils/uuid';
import type { ISchemaContainer } from './schemaContainer';
import { countries } from '../utils/countries';
import { replaceKoiError } from './replaceKoiError';
import { emailSchema } from './schemas/emailSchema';
import { agbCodeSchema } from './schemas/identifierDeclarationSchemas';
import { phoneNumberSchema } from './schemas/phoneNumberSchema';
import { relationNumberSchema } from './schemas/relationNumberSchema';
import { dateRangesHaveOverlap } from './utils/dateRangesHaveOverlap';

export const agbCodeRequiredTypes = [OrganizationType.gpPractice, OrganizationType.hospital];
export const agbCodeRequiredStructureTypes = [OrganizationStructureType.branch, OrganizationStructureType.organization];
export const kgoStatusCommentRequired = [OrganizationKgoStatus.attentionRequired, OrganizationKgoStatus.inProgress];

export function isAgbCodeRequiredForOrganizationProps({
    organizationType,
    organizationStructureType,
    isActive,
    kgoStatus,
}: {
    isActive?: boolean | null;
    kgoStatus?: OrganizationKgoStatus | null;
    organizationStructureType?: OrganizationStructureType | null;
    organizationType?: OrganizationType | null;
}): boolean {
    return (
        agbCodeRequiredTypes.some((type) => type === organizationType) &&
        agbCodeRequiredStructureTypes.some((structureType) => structureType === organizationStructureType) &&
        (isNonNullable(isActive) === false || isActive) &&
        (isNonNullable(kgoStatus) === false || kgoStatus === OrganizationKgoStatus.verified)
    );
}

export type OrganizationAgbCodeValidated = {
    agbCode: string;
    endDate: string | null;
    startDate: string;
};

export const organizationAgbCodeSchema: ISchemaContainer<OrganizationAgbCodeValidated> = {
    schema: Koi.object({
        agbCode: agbCodeSchema,
        endDate: Koi.koi().endDate().allow(null),
        startDate: Koi.koi().date().required(),
    }),
};

export type OrganizationRelationshipValidated = {
    id?: Uuid;
    isDeleted?: boolean;
    relatedOrganization?: {
        id: Uuid;
        name: string;
    };
    relatedOrganizationId: Uuid;
    type: OrganizationRelationshipType;
};

export const organizationRelationshipSchema: ISchemaContainer<OrganizationRelationshipValidated> = {
    schema: Koi.object({
        id: Koi.string().uuid().optional(),
        isDeleted: Koi.boolean().optional(),
        relatedOrganization: Koi.object({
            id: Koi.string().uuid().required(),
            name: Koi.string().max(255).required(),
        }).optional(),
        relatedOrganizationId: Koi.string()
            .uuid()
            .required()
            .custom((value, helpers) => {
                return value === emptyUuid ? helpers.error('any.required') : value;
            }),
        type: Koi.koi().enum(OrganizationRelationshipType).required(),
    }),
};

const agbCodesOptionalSchema = Koi.array()
    .items(organizationAgbCodeSchema.schema)
    .custom((value, helpers) => (dateRangesHaveOverlap(value) ? helpers.error('dateRanges.hasOverlap') : value))
    .error((errors) => replaceKoiError(errors, 'array.includesRequiredUnknowns', 'agbCodeRequired'))
    .optional()
    .allow(null);

const agbCodesRequiredSchema = Koi.array()
    .items(organizationAgbCodeSchema.schema)
    .custom((value, helpers) => (dateRangesHaveOverlap(value) ? helpers.error('dateRanges.hasOverlap') : value))
    .custom((value, helpers) =>
        hasActiveItem(value, DateTime.today().toDate()) ? value : helpers.error('dateRanges.noActiveItem')
    )
    .error((errors) => replaceKoiError(errors, 'array.includesRequiredUnknowns', 'agbCodeRequired'))
    .error((errors) => replaceKoiError(errors, 'array.base', 'dateRanges_noActiveItem'));

const agbCodesSchema = Koi.when(Koi.ref('..'), {
    is: Koi.object({
        isActive: true,
        kgoStatus: OrganizationKgoStatus.verified,
        organizationStructureType: Koi.valid(...agbCodeRequiredStructureTypes),
        organizationType: Koi.valid(...agbCodeRequiredTypes),
    }),
    otherwise: agbCodesOptionalSchema,
    then: agbCodesRequiredSchema,
});

export const organizationSchema: ISchemaContainer<IOrganizationValidated> = {
    schema: Koi.object({
        agbCodes: agbCodesSchema,
        alias: Koi.string().max(255).allow(null).optional(),
        areContactsAndTasksEnabled: Koi.boolean(),
        city: Koi.string().max(255),
        country: Koi.string()
            .max(2)
            .valid(...countries.map((country) => country.value))
            .allow(null)
            .optional(),
        description: Koi.string().max(1000).allow(null).optional(),
        emailAddressPayouts: emailSchema.allow(null).optional(),
        emailAddressRequestInbox: emailSchema,
        emergencyNumber: phoneNumberSchema.allow(null).optional(),
        houseNumber: Koi.string().max(255).allow(null).optional(),
        houseNumberSuffix: Koi.string().max(255).allow(null).optional(),
        isActive: Koi.boolean(),
        isHighPrivacy: Koi.boolean(),
        isMijnKsyosActive: Koi.boolean(),
        kgoStatus: Koi.koi().enum(OrganizationKgoStatus),
        kgoStatusComment: Koi.when('kgoStatus', {
            is: kgoStatusCommentRequired,
            otherwise: Koi.string().max(1000).allow(null).optional(),
            then: Koi.string().max(1000),
        }),
        kvkNumber: Koi.string()
            .regex(kvkNumberRegex)
            .allow(null)
            .optional()
            .error((errors) => replaceKoiError(errors, 'string.pattern.base', 'string.kvkNumber')),
        name: Koi.string().max(255),
        nameStatutory: Koi.string().max(255).allow(null).optional(),
        organizationRelationships: Koi.array().items(organizationRelationshipSchema.schema),
        organizationStructureType: Koi.koi().enum(OrganizationStructureType),
        organizationType: Koi.koi().enum(OrganizationType),
        phoneNumber: phoneNumberSchema,
        relationNumber: relationNumberSchema,
        state: Koi.string()
            .max(255)
            .when('country', {
                is: 'NL',
                otherwise: Koi.allow(null).optional(),
                then: Koi.required(),
            }),

        street: Koi.string().max(255).allow(null).optional(),
        suppressEmployeePayoutOverview: Koi.boolean().default(false).optional(),
        url: Koi.string().max(255).allow(null).optional(),
        zip: Koi.string()
            .max(255)
            .when('country', {
                is: 'NL',
                then: Koi.string().zipCodeNL(true),
            }),
    }).options({ convert: false, presence: 'required' }),
};

export interface IOrganizationValidated {
    agbCodes?: OrganizationAgbCodeValidated[];
    alias?: string | null;
    areContactsAndTasksEnabled: boolean;
    city: string;
    country?: string | null;
    description?: string | null;
    emailAddressPayouts?: string | null;
    emailAddressRequestInbox: string;
    emergencyNumber?: string | null;
    houseNumber?: string | null;
    houseNumberSuffix?: string | null;
    isActive: boolean;
    isHighPrivacy: boolean;
    isMijnKsyosActive: boolean;
    kgoStatus?: OrganizationKgoStatus | null;
    kgoStatusComment?: string | null;
    kvkNumber?: string | null;
    name: string;
    nameStatutory?: string | null;
    organizationRelationships: OrganizationRelationshipValidated[];
    organizationStructureType: OrganizationStructureType;
    organizationType: OrganizationType;
    phoneNumber: string;
    relationNumber?: string | null;
    state?: string | null;
    street?: string | null;
    suppressEmployeePayoutOverview?: boolean;
    url?: string | null;
    zip: string;
}

export interface IOrganizationKsyosAccountValidated {
    areContactsAndTasksEnabled: boolean;
    isActive: boolean;
    isHighPrivacy: boolean;
    isMijnKsyosActive: boolean;
}

export interface IOrganizationKsyosAccountUpdateValidated {
    areContactsAndTasksEnabled: boolean;
    authorization: {
        canUpdateProperties: string[];
    };
    isActive: boolean;
    isHighPrivacy: boolean;
    isMijnKsyosActive: boolean;
}

const organizationKsyosAccountUpdate = Koi.object().keys({
    areContactsAndTasksEnabled: Koi.boolean(),
    authorization: Koi.object()
        .keys({
            canUpdateProperties: Koi.array().required(),
        })
        .required(),
    isActive: Koi.when('authorization.canUpdateProperties', {
        is: Koi.array().items(Koi.string().valid('isActive').required(), Koi.string()),
        otherwise: Koi.forbidden(),
        then: Koi.boolean(),
    }),
    isHighPrivacy: Koi.boolean(),
    isMijnKsyosActive: Koi.boolean(),
});
export const organizationKsyosAccountUpdateSchema: ISchemaContainer<IOrganizationKsyosAccountUpdateValidated> = {
    schema: organizationKsyosAccountUpdate.options({ convert: false, presence: 'required' }),
};

export const organizationFiltersValidation = Koi.object({
    agbCode: Koi.string().max(255),
    cityOrZip: Koi.string().max(255),
    episodeCategoryIds: Koi.array().items(Koi.string().uuid()),
    groupIds: Koi.array().items(Koi.string().uuid()),
    hasActiveAnalyst: Koi.boolean(),
    houseNumber: Koi.string().max(255),
    houseNumberSuffix: Koi.string().max(255),
    include: Koi.string().uuid(),
    isActive: Koi.koi().enum(ActiveOrInactive),
    isKsyosOrganizationExcluded: Koi.boolean(),
    kgoStatuses: Koi.array().items(Koi.koi().enum(OrganizationKgoStatus)),
    kvkNumber: Koi.string().regex(kvkNumberRegex),
    matchType: Koi.koi().enum(MatchType),
    name: Koi.string().max(255),
    nameOrId: Koi.string().max(255),
    nameStatutory: Koi.string().max(255),
    organizationStructureTypes: Koi.array().items(Koi.koi().enum(OrganizationStructureType)),
    organizationTypes: Koi.array().items(Koi.koi().enum(OrganizationType)),
    providedService: Koi.koi().enum(ServiceCode),
    relationNumber: Koi.string().max(255),
    searchText: Koi.string().max(255),
    serviceGroupIds: Koi.array().items(Koi.string().uuid()),
    specialism: Koi.string(),
    states: Koi.array().items(Koi.koi().enum(State)),
});

export interface IOrganizationFilters {
    agbCode?: string;
    cityOrZip?: string;
    episodeCategoryIds?: string[];
    groupIds?: string[];
    hasActiveAnalyst?: boolean;
    houseNumber?: string;
    houseNumberSuffix?: string;
    include?: string;
    isActive?: ActiveOrInactive;
    isKsyosOrganizationExcluded?: boolean;
    kgoStatuses?: OrganizationKgoStatus[];
    kvkNumber?: string;
    matchType?: MatchType;
    name?: string;
    nameOrId?: string;
    nameStatutory?: string;
    organizationStructureTypes?: OrganizationStructureType[];
    organizationTypes?: OrganizationType[];
    providedService?: ServiceCode;
    relationNumber?: string;
    searchText?: string;
    serviceGroupIds?: string[];
    specialism?: string;
    states?: string[];
}

export interface IOrganizationGeneralSettings
    extends Pick<
        IOrganizationValidated,
        | 'agbCodes'
        | 'alias'
        | 'description'
        | 'isActive'
        | 'kvkNumber'
        | 'name'
        | 'nameStatutory'
        | 'organizationStructureType'
        | 'organizationType'
        | 'organizationRelationships'
        | 'kgoStatus'
        | 'kgoStatusComment'
        | 'city'
        | 'country'
        | 'emailAddressRequestInbox'
        | 'emergencyNumber'
        | 'houseNumber'
        | 'houseNumberSuffix'
        | 'phoneNumber'
        | 'state'
        | 'street'
        | 'url'
        | 'zip'
    > {
    nameStartDate?: string;
}

export const organizationGeneralSettingsSchema: ISchemaContainer<IOrganizationGeneralSettings> = {
    schema: pickKeysAndCreateSchema(organizationSchema.schema, [
        'agbCodes',
        'alias',
        'description',
        'isActive',
        'kgoStatus',
        'kgoStatusComment',
        'kvkNumber',
        'name',
        'nameStatutory',
        'organizationStructureType',
        'organizationType',
        'organizationRelationships',
        'city',
        'country',
        'emailAddressRequestInbox',
        'emergencyNumber',
        'houseNumber',
        'houseNumberSuffix',
        'phoneNumber',
        'state',
        'street',
        'url',
        'zip',
    ])
        .keys({
            nameStartDate: Koi.koi().date().optional(),
        })
        .options({ allowUnknown: true, stripUnknown: true }),
};

export const startDateSchemaFactory = (maxDate: DateTime) => ({
    schema: Koi.object().keys({
        startDate: Koi.koi()
            .dateSameOrBefore(maxDate.startOf('day').format('YYYY-MM-DD'))
            .required()
            .error((e: Joi.ErrorReport[]) => replaceKoiError(e, 'koi.date', 'chooseCurrentDateOrBefore')),
    }),
});

export interface IOrganizationFinancialSettings {
    emailAddressPayouts: string | null;
    organizationLevelPayoutsStartDate: string | null;
    relationNumber: string | null;
    suppressEmployeePayoutOverview: boolean;
}

const organizationFinancialSettings = Koi.object().keys({
    emailAddressPayouts: emailSchema.allow(null).optional(),
    organizationLevelPayoutsStartDate: Koi.koi().date().allow(null).optional(),
    relationNumber: relationNumberSchema,
    suppressEmployeePayoutOverview: Koi.boolean(),
});

export const organizationFinancialSettingsSchema: ISchemaContainer<IOrganizationFinancialSettings> = {
    schema: organizationFinancialSettings.options({ convert: false, presence: 'required' }),
};
